import {
    DISCOVERY_PAIN_POINTS_CONTAINER_ID,
    DISCOVERY_QUESTIONS_SECTION_ID,
    PAIN_POINTS_SECTION_ID,
    StrategyDisplayStateEnum,
} from './Strategy.constants';
import { SalesStrategySection, SalesStrategyState } from '@apiHooks/useSalesStrategy';

export function getStrategyDisplayState(
    isLoading: boolean,
    apiState: SalesStrategyState,
    isGenerating: boolean = false,
    hasError: boolean = false,
    hasSections: boolean = false,
): StrategyDisplayStateEnum {
    if (isGenerating) {
        return StrategyDisplayStateEnum.Generating;
    }

    if (isLoading) {
        return StrategyDisplayStateEnum.Loading;
    }

    if (hasSections) {
        return StrategyDisplayStateEnum.Ready;
    }

    if (apiState === 'InProgress' && !hasError) {
        return StrategyDisplayStateEnum.Generating;
    }

    if (apiState === 'Ready') {
        return StrategyDisplayStateEnum.Ready;
    }

    return StrategyDisplayStateEnum.Empty;
}

export function isStrategyIncomplete(state: SalesStrategyState, isPartial: boolean): boolean {
    return isPartial || state === 'InProgress';
}

export function toTitleCase(input: string): string {
    return input
        .toLowerCase()
        .split(/\s+/)
        .map(word => (word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
        .join(' ');
}

function buildSubHeadingHtml(section: SalesStrategySection): string {
    return `<h3 class="StrategyContent__sub-heading">${toTitleCase(section.title ?? '')}</h3>${section.html ?? ''}`;
}

export function mergeDiscoveryAndPainPointsSections(sections: SalesStrategySection[]): SalesStrategySection[] {
    const containerSection = sections.find(section => section.id === DISCOVERY_PAIN_POINTS_CONTAINER_ID);
    const discoverySection = sections.find(section => section.id === DISCOVERY_QUESTIONS_SECTION_ID);
    const painPointsSection = sections.find(section => section.id === PAIN_POINTS_SECTION_ID);

    if (!containerSection || (!discoverySection && !painPointsSection)) {
        return sections;
    }

    const mergedHtml = [discoverySection, painPointsSection]
        .filter((section): section is SalesStrategySection => !!section)
        .map(buildSubHeadingHtml)
        .join('');

    return sections
        .filter(section => section.id !== DISCOVERY_QUESTIONS_SECTION_ID && section.id !== PAIN_POINTS_SECTION_ID)
        .map(section => (section.id === DISCOVERY_PAIN_POINTS_CONTAINER_ID ? { ...section, html: mergedHtml } : section));
}

export interface StrategyContentSections {
    overviewSections: SalesStrategySection[];
    detailSections: SalesStrategySection[];
    canExpand: boolean;
}

export function getStrategyContentSections(sections: SalesStrategySection[]): StrategyContentSections {
    const mergedSections = mergeDiscoveryAndPainPointsSections(sections);
    const overviewSections = mergedSections.filter(section => section.isOverview);
    const detailSections = mergedSections.filter(section => !section.isOverview);
    const canExpand = mergedSections.length > 0;

    return { overviewSections, detailSections, canExpand };
}

export function formatReferenceLinks(html: string): string {
    const withoutLabels = html.replace(
        /\[\d+\]\s*[^<>\n]+?\s*\n\s*URL:\s*(?=<a\s)/gi,
        match => match.match(/^\[\d+\]\s*/)?.[0] ?? '',
    );

    return withoutLabels.replace(/<p>([\s\S]*?)<\/p>/gi, (fullMatch, inner: string) => {
        const entries = inner
            .split(/\s*<br\s*\/?>\s*/i)
            .map(entry => entry.trim())
            .filter(Boolean);

        if (entries.length <= 1) {
            return fullMatch;
        }

        return entries.map(entry => `<p>${entry}</p>`).join('');
    });
}

export function addClassesToHtmlTags(html: string, tagClassMap: Record<string, string>): string {
    return Object.entries(tagClassMap).reduce((currentHtml, [tag, classes]) => {
        const tagPattern = new RegExp(`<${tag}\\b([^>]*)>`, 'gi');
        const classList = classes.trim().split(/\s+/);

        return currentHtml.replace(tagPattern, (fullMatch, attributes: string) => {
            const trimmedAttributes = attributes.trim();
            const classAttributeMatch = trimmedAttributes.match(/\bclass\s*=\s*(['"])(.*?)\1/i);

            if (classAttributeMatch) {
                const existingClasses = classAttributeMatch[2].trim().split(/\s+/).filter(Boolean);
                const mergedClasses = Array.from(new Set([...existingClasses, ...classList]));
                const newAttributes = trimmedAttributes.replace(
                    /\bclass\s*=\s*(['"]).*?\1/i,
                    `class="${mergedClasses.join(' ')}"`,
                );

                return `<${tag} ${newAttributes}>`;
            }

            const newAttributes =
                trimmedAttributes !== ''
                    ? `${trimmedAttributes} class="${classList.join(' ')}"`
                    : `class="${classList.join(' ')}"`;

            return `<${tag} ${newAttributes}>`;
        });
    }, html);
}
